import { Injectable } from '@angular/core';
import { AppConfig } from '../../shared/config/app-config';
import { LocalStorageService } from './local-storage.service';
import { Subject } from 'rxjs';
import { StorageKeys } from '../../shared/models/storage-keys';
import { MoniepointAppParameters } from '../models/login.model';
import { DelegateType } from '../../shared/models/utility.model';
import { MobileDelegateService } from './mobile-delegate.service';
import 'clientjs';
import { DataLayerService } from './data-layer-service.service';
import { isMoniepointAppUser, moniepointAppParameters } from '../../shared/models/mobile-app-helper.model';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { logger } from '@mpt-fo/common/telemetry';

@Injectable({
    providedIn: 'root',
})
export class MobileWebViewService {
    static readonly BASE_URL = '/mobile-web-view';
    bvnOnbaordingKey: any;
    mobile: boolean;
    deviceIdentifier: any;
    readonly APP_STORE_LINK = 'https://cutt.ly/moniepointapp';
    readonly LIVELINESS_APP_VERSION = '1.1.5';
    readonly FILE_PICKER_APP_VERSION = '1.1.7';
    loggedOut = false;
    savedUsername: string;
    setupBiometric: any;
    loginParameters: MoniepointAppParameters;
    runningModal: NzModalRef;
    private deviceInformation: { deviceUniqueIdentifier; manufacturer; model; deviceType? };
    private registerBiometric: boolean;
    private biometricType: BiometricType;
    private deviceRegistrationParameters: any;
    private changePasswordCredentials: { username; validationKey; password };
    // tslint:disable-next-line:variable-name
    private _signUpBackButton = new Subject<any>();
    // tslint:disable-next-line:variable-name
    _signUpBackButton$ = this._signUpBackButton.asObservable();

    constructor(
        public config: AppConfig,
        private localStorage: LocalStorageService,
        private delegateService: MobileDelegateService,
        private dataLayerHelper: DataLayerService,
    ) {}

    saveMoniepointAppParameters(appParameters: MoniepointAppParameters) {
        this.localStorage.save('kyc-provision-primer-mobile-login-parameters', JSON.stringify(appParameters));
        this.loginParameters = appParameters;
    }

    setMobileAppVersion(version: string) {
        this.localStorage.save('mobile_App_Version', version);
    }

    setClientType(id) {
        id = id || 'WEB';
        this.localStorage.save('device_client_type', id);
    }

    getMobileAppVersion() {
        return this.localStorage.get('mobile_App_Version') || null;
    }

    isAppLowerVersion(defaultVersion: string, currentVersion) {
        const appV1 = defaultVersion.split('.');
        const appV2 = currentVersion.split('.');
        const version1 = Number(appV1.join(''));
        const version2 = Number(appV2.join(''));
        return version1 >= version2;
    }

    getMoniepointAppParameters(): MoniepointAppParameters {
        const appParameters = moniepointAppParameters();
        this.logFailedAppDeviceId(appParameters);
        return appParameters;
    }

    buildDeviceIdEventParams(eventData) {
        return {
            event: 'device_id_failed',
            email: this.localStorage.get(StorageKeys.email),
            device_type: eventData?.model,
            device_id: eventData?.deviceId,
            username: eventData?.username,
            phone_number: this.localStorage.get(StorageKeys.businessNumber),
            user_agent: eventData?.userAgent,
            mobile_version: eventData?.appVersion,
        };
    }

    logFailedAppDeviceId(param: MoniepointAppParameters) {
        const deviceRegistrationRequest = param?.deviceRegistrationRequest;
        const model = deviceRegistrationRequest?.model?.toUpperCase();
        const params = {
            deviceId: deviceRegistrationRequest?.deviceUniqueIdentifier,
            model,
            userAgent: param?.userAgent,
            username: deviceRegistrationRequest?.username,
            appVersion: param?.appVersion,
        };
        const eventPayload = this.buildDeviceIdEventParams(params);
        this.dataLayerHelper.logEvent(eventPayload);
    }

    setBiometricType(biometricType: BiometricType) {
        this.biometricType = biometricType;
    }

    setNotificationUrl(url) {
        this.storeDataMobileStorage('notification-url', url);
    }

    setUnReadNotification(isUnreadNotification: boolean) {
        this.localStorage.save('has-unread-notification', JSON.stringify(isUnreadNotification));
    }

    setNotification(notification: any) {
        this.localStorage.save('notification-content', JSON.stringify(notification));
    }

    storeDataMobileStorage(key, data) {
        if (!this.isMobileWebViewUser()) {
            return null;
        }
        if (!data) {
            return;
        }
        const toSave = JSON.stringify(data);
        try {
            this.delegateService.setDelegateData(DelegateType.saveValue, key, toSave).catch((err) => {
                logger.error('Error calling saveValue delegate ---> ', err);
            });
        } catch (err) {
            logger.error('Error calling storeDataMobileStorage --->', err);
        }
    }

    async getSavedDataMobileStorage(key) {
        if (!this.isMobileWebViewUser()) {
            return null;
        }
        let data: any;
        try {
            data = await this.delegateService.setDelegateData(DelegateType.getValue, key);
        } catch (err) {
            logger.error('Error getting getSavedDataMobileStorage ---> ', err);
        }
        return data;
    }

    getNotification() {
        const savedNotification = this.localStorage.get('notification-content');
        if (!savedNotification) {
            return null;
        }
        return JSON.parse(savedNotification);
    }

    setCanShowNotificationList(show) {
        this.localStorage.save('CAN_SHOW_NOTIFICATION_LIST', JSON.stringify(show));
    }

    canShowNotificationList() {
        const saved = this.localStorage.get('CAN_SHOW_NOTIFICATION_LIST');
        if (!saved) {
            return false;
        }
        return JSON.parse(saved);
    }

    signUpStepLoading(loading: boolean) {
        this._signUpBackButton.next(loading);
    }

    setChangePasswordCredential(credentials: { username; validationKey; password }) {
        this.changePasswordCredentials = credentials;
        this.localStorage.save('password-recovery-credentials', JSON.stringify(credentials));
    }

    getChangePasswordCredential() {
        const credentials = { username: null, validationKey: null, password: null };
        let savedCredentials = this.localStorage.get('password-recovery-credentials');
        savedCredentials = savedCredentials ? JSON.parse(savedCredentials) : credentials;
        return this.changePasswordCredentials || savedCredentials;
    }

    clearChangePasswordCredential() {
        this.changePasswordCredentials = null;
        this.localStorage.deleteValue('password-recovery-credentials');
    }

    setDeviceRegistrationParameters(params) {
        this.deviceRegistrationParameters = params;
    }

    getDeviceRegistrationParameters() {
        return this.deviceRegistrationParameters;
    }

    setLoggedOut(loggedOut) {
        this.localStorage.save(StorageKeys.is_mobile_web_view_Logout, JSON.stringify(loggedOut));
        this.loggedOut = loggedOut;
    }

    setHasSetupBiometric(setupBiometric: boolean) {
        this.setupBiometric = setupBiometric;
    }

    setSavedUsername(username) {
        this.localStorage.save('returning-user', username || '');
        this.savedUsername = username;
    }

    getBiometricType() {
        return this.biometricType;
    }

    setRegisterBiometric(register) {
        this.localStorage.save('can-register-biometric', JSON.stringify(register));
        this.registerBiometric = register;
    }

    canRegisterBiometric() {
        return this.registerBiometric || this.getSavedRegistration();
    }

    setFlutterInAppWebViewPlatformReady(flutterInAppWebViewPlatformReady) {
        this.storeDataMobileStorage('flutter-InAppWebView-PlatformReady', JSON.stringify(flutterInAppWebViewPlatformReady));
    }

    async isFlutterInAppWebViewPlatformReady() {
        const flutterInAppWebView = await this.getSavedDataMobileStorage('flutter-InAppWebView-PlatformReady');
        if (flutterInAppWebView) {
            return false;
        }
        return JSON.parse(flutterInAppWebView);
    }

    getDeviceInformation() {
        let savedDeviceInformation = this.localStorage.get('mobile-device-information');
        savedDeviceInformation = savedDeviceInformation ? JSON.parse(savedDeviceInformation) : null;
        return this.deviceInformation || savedDeviceInformation;
    }

    isMobile() {
        return this.mobile;
    }

    setMobile(mobile) {
        this.mobile = mobile;
    }

    isMobileWebViewUser() {
        return this.isMoniepointMobileAppUser() && this.config.SHOW_MOBILE_WEB_VIEW;
    }

    buildUpdateAppModalParams() {
        return {
            title: 'Old App detected',
            description: 'In order to continue with liveliness check you are required to update your app',
            alertType: 'warning',
            callbackName: 'Update App',
        };
    }

    launchUpdateAppModal() {
        const params: any = this.buildUpdateAppModalParams();
        // const config = new ModalOptionsConfig();
        // config.nzComponentParams.alertType = params?.alertType || 'error';
        // config.nzComponentParams.title = params?.title;
        // config.className = DEFAULT_MODAL_KAMONA_CLASSES;
        // config.nzWidth = DEFAULT_MODAL_WIDTH;
        // config.nzComponentParams.callback = () => {
        // 	this.handleCloseUpdateAppModal();
        // };
        // config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        // config.nzComponentParams.callbackName = params?.callbackName || 'Close';
        // config.nzComponentParams.message = params?.description;
        // this.runningModal = this.modalService.alert(config);
    }

    handleCloseUpdateAppModal() {
        // window.open(`${this.APP_STORE_LINK}`, '_blank');
        // this.modalService.closeAll();
    }

    private isMoniepointMobileAppUser() {
        return isMoniepointAppUser();
    }

    private getSavedRegistration() {
        let canRegister = this.localStorage.get('can-register-biometric');
        canRegister = canRegister ? JSON.parse(canRegister) : false;
        return canRegister;
    }
}

export enum MobileWebViewEvent {
    getDeviceInformation = 'getDeviceInformation',
    flutterInAppWebViewPlatformReady = 'flutterInAppWebViewPlatformReady',
    saveOnboardingInfo = 'saveOnboardingInfo',
    getOnboardingInfo = 'getOnboardingInfo',
}

export enum BiometricType {
    FINGER_PRINT = 'FINGER_PRINT',
    FACE_ID = 'FACE_ID',
    NONE = 'NONE',
}

export enum AnimationDirection {
    FORWARD = 'animate__animated animate__slideInLeft',
    BACKWARD = 'animate__animated animate__slideInRight',
}
